import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowsClockwise, ArrowClockwise, MagnifyingGlass, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";

/**
 * Iter-63.1 — Admin Failed Deliveries operational surface.
 *
 * Backed by `GET /api/delivery/failed-attempts` (paginated cursor).
 * Displays retrying / failed_final / abandoned attempts with lead + destination
 * links, and offers a Manual Repush action for eligible failed_final /
 * abandoned rows via `POST /api/delivery/attempts/{id}/repush`.
 *
 * Never repushes `delivered` attempts through the normal action button —
 * that path is guarded server-side and rejected with 409.
 */
export default function FailedDeliveries() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, page_size: 25, has_next: false });
  const [state, setState] = useState("all");
  const [destId, setDestId] = useState("");
  const [clientId, setClientId] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [repushing, setRepushing] = useState(null);
  const [repushDialog, setRepushDialog] = useState(null); // {attempt, reason}

  const isInternal = user?.org_type === "internal";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("state", state);
      params.set("page", String(page));
      params.set("page_size", "25");
      if (destId) params.set("destination_id", destId);
      if (clientId) params.set("client_id", clientId);
      const { data } = await api.get(`/delivery/failed-attempts?${params.toString()}`);
      setRows(data.attempts || []);
      setPagination(data.pagination || { total: 0, page: 1, page_size: 25, has_next: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [state, page, destId, clientId]);

  useEffect(() => { setPage(1); }, [state, destId, clientId]);
  useEffect(() => { if (isInternal) load(); }, [load, isInternal]);

  const openRepush = (attempt) => setRepushDialog({ attempt, reason: "" });
  const closeRepush = () => setRepushDialog(null);

  const submitRepush = async () => {
    if (!repushDialog) return;
    const { attempt, reason } = repushDialog;
    setRepushing(attempt.id);
    try {
      const { data } = await api.post(`/delivery/attempts/${attempt.id}/repush`, { reason });
      if (data.delivered) {
        toast.success("Repushed successfully");
      } else {
        toast.warning(`Repush attempted — response ${data.code}`);
      }
      closeRepush();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Repush failed");
    } finally {
      setRepushing(null);
    }
  };

  if (!isInternal) {
    return (
      <div className="brut-card p-8 text-center label-mono" data-testid="failed-deliveries-forbidden">
        Failed Deliveries is an internal-only surface.
      </div>
    );
  }

  const total = pagination.total || 0;
  const start = total === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
  const end = (pagination.page - 1) * pagination.page_size + rows.length;

  return (
    <div data-testid="failed-deliveries-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-mono">Ops · retries + failures</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">
            Failed deliveries
          </h1>
          <div className="mt-1 text-sm text-zinc-600 max-w-3xl">
            Every delivery that is retrying or failed, with the buyer, the landing page, why it failed and what
            happened next. When a buyer still fails after every retry, the lead is offered to the next matching
            buyer (unless the visitor agreed to that firm only) and the admins get an email.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/leads?delivery_status=unmatched" data-testid="failed-deliveries-no-buyer-link"
            className="border border-black px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-black hover:text-white">
            Leads no buyer took →
          </Link>
          <Link to="/delivery-health" data-testid="failed-deliveries-back-to-health"
            className="border border-black px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-black hover:text-white">
            ← Delivery health
          </Link>
          <button onClick={load} data-testid="failed-deliveries-refresh"
            className="border border-black px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-black hover:text-white">
            <ArrowsClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      <div className="brut-card p-3 mb-3 flex flex-col md:flex-row md:items-center gap-2 md:flex-wrap"
           data-testid="failed-deliveries-toolbar">
        <div className="flex items-center gap-1">
          {[
            { k: "retrying",      label: "Retrying" },
            { k: "failed_final",  label: "Failed final" },
            { k: "abandoned",     label: "Abandoned" },
            { k: "all",           label: "All" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setState(t.k)}
              data-testid={`failed-deliveries-state-${t.k}`}
              className={`border border-black px-2 py-1.5 text-[11px] uppercase tracking-widest ${
                state === t.k ? "bg-black text-white" : "hover:bg-black hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input type="text" value={destId} onChange={(e) => setDestId(e.target.value)}
            placeholder="Filter by destination id"
            className="w-full border border-black pl-7 pr-2 py-1.5 text-sm font-mono"
            data-testid="failed-deliveries-dest-filter" />
        </div>
        <input type="text" value={clientId} onChange={(e) => setClientId(e.target.value)}
          placeholder="Filter by buyer client_id"
          className="w-56 border border-black px-2 py-1.5 text-sm font-mono"
          data-testid="failed-deliveries-client-filter" />
        {(destId || clientId) && (
          <button onClick={() => { setDestId(""); setClientId(""); }}
            data-testid="failed-deliveries-clear"
            className="border border-black px-2 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1">
            <X size={12} /> Clear
          </button>
        )}
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm font-mono min-w-[1200px]" data-testid="failed-deliveries-table">
          <thead>
            <tr className="border-b border-black text-[10px] uppercase tracking-widest">
              <th className="py-2 px-3 text-left">Lead</th>
              <th className="py-2 px-3 text-left">Buyer</th>
              <th className="py-2 px-3 text-left">Landing page</th>
              <th className="py-2 px-3 text-left">Status</th>
              <th className="py-2 px-3 text-left">Why it failed</th>
              <th className="py-2 px-3 text-left">What happened next</th>
              <th className="py-2 px-3 text-left">When</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isRepushable = r.status === "failed_final" || r.status === "abandoned";
              return (
                <tr key={r.id} className="border-b border-zinc-200 hover:bg-zinc-50"
                    data-testid={`failed-deliveries-row-${r.id}`}>
                  <td className="py-2 px-3">
                    {r.lead_id ? (
                      <Link to={`/leads/${r.lead_id}`} className="underline hover:text-emerald-700"
                            data-testid={`failed-deliveries-lead-${r.id}`}>
                        {r.lead_name || r.lead_id.slice(0, 8)}
                      </Link>
                    ) : "—"}
                    <div className="text-[10px] text-zinc-500">{r.lead_id?.slice(0, 8)}{r.lead_state ? ` · ${r.lead_state}` : ""}</div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="font-semibold">{r.company || r.destination_name || r.destination_id?.slice(0, 8) || "—"}</div>
                    <div className="text-[10px] text-zinc-500">{r.destination_name} · {r.destination_type} · try #{r.attempt_no || 1}</div>
                  </td>
                  <td className="py-2 px-3 text-[11px]" data-testid={`failed-deliveries-landing-${r.id}`}>{r.landing_page || "—"}</td>
                  <td className="py-2 px-3">
                    <span className={
                      "text-[9px] uppercase tracking-widest border px-1.5 py-0.5 " +
                      (r.status === "failed_final" || r.status === "abandoned"
                        ? "bg-red-600 border-red-600 text-white"
                        : "bg-yellow-100 border-yellow-700 text-yellow-900")
                    }>
                      {r.status === "retrying" ? "retrying" : "failed"}
                    </span>
                    {r.lead_delivery_status === "delivered" && (
                      <div className="mt-1 text-[10px] text-emerald-800" data-testid={`failed-deliveries-lead-ok-${r.id}`}>Lead reached a buyer</div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-[11px] max-w-[260px]" data-testid={`failed-deliveries-reason-${r.id}`}>
                    {r.reason || r.response_code || "—"}
                  </td>
                  <td className="py-2 px-3 text-[11px] max-w-[260px]" data-testid={`failed-deliveries-next-${r.id}`}>
                    {r.next_step || (r.status === "retrying" ? `Will try again ${r.next_retry_at ? fmtDate(r.next_retry_at) : "soon"}` : "—")}
                  </td>
                  <td className="py-2 px-3 text-[11px]">{fmtDate(r.created_at)}</td>
                  <td className="py-2 px-3 text-right">
                    <div className="inline-flex flex-wrap justify-end gap-1">
                      {isRepushable && (
                        <button onClick={() => openRepush(r)}
                          disabled={repushing === r.id}
                          data-testid={`failed-deliveries-repush-${r.id}`}
                          className="border border-emerald-700 text-emerald-800 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-700 hover:text-white inline-flex items-center gap-1 disabled:opacity-40">
                          <ArrowClockwise size={11} weight="bold" /> Resend
                        </button>
                      )}
                      {r.lead_id && (
                        <Link to={`/leads/${r.lead_id}?tab=delivery`} data-testid={`failed-deliveries-send-other-${r.id}`}
                          className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                          Send to a buyer
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && !rows.length && (
              <tr><td colSpan={8} className="py-8 text-center label-mono"
                      data-testid="failed-deliveries-empty">
                No {state} attempts match those filters.
              </td></tr>
            )}
            {loading && <tr><td colSpan={8} className="py-8 text-center label-mono">Loading…</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs font-mono uppercase tracking-widest text-zinc-700">
        <div data-testid="failed-deliveries-count">
          {total === 0 ? "No attempts" : `Showing ${start}${start !== end ? `–${end}` : ""} of ${total}`}
        </div>
        <div className="flex items-center gap-2" data-testid="failed-deliveries-pagination">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
            data-testid="failed-deliveries-page-prev"
            className="border border-black px-2 py-1 disabled:opacity-30 hover:bg-black hover:text-white">Prev</button>
          <span>Page {pagination.page || 1}</span>
          <button onClick={() => setPage(page + 1)} disabled={!pagination.has_next}
            data-testid="failed-deliveries-page-next"
            className="border border-black px-2 py-1 disabled:opacity-30 hover:bg-black hover:text-white">Next</button>
        </div>
      </div>

      {repushDialog && (
        <div className="fixed inset-0 z-40 flex items-start justify-center pt-16 bg-black/50"
             data-testid="failed-deliveries-repush-modal">
          <div className="bg-white border-2 border-black brut-shadow-md max-w-lg w-full mx-4 p-5">
            <div className="font-chivo font-black uppercase tracking-tighter mb-3">
              Manual repush
            </div>
            <div className="text-xs text-zinc-700 mb-4 leading-relaxed">
              This will create a NEW delivery attempt using the current lead
              payload. It will NOT re-bill the buyer, will NOT re-credit the
              affiliate, and will NOT touch invoice lines. If the destination is
              inactive or the emergency pause is active, the repush will be
              rejected.
            </div>
            <label className="label-mono">Reason (audit annotation)</label>
            <textarea rows={3}
              className="w-full border border-black px-2 py-1.5 mt-1 text-sm"
              placeholder="e.g. Buyer confirmed intake system was down 08:00-09:00 UTC"
              value={repushDialog.reason}
              onChange={(e) => setRepushDialog({ ...repushDialog, reason: e.target.value })}
              data-testid="failed-deliveries-repush-reason" />
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={closeRepush} disabled={!!repushing}
                data-testid="failed-deliveries-repush-cancel"
                className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white">
                Cancel
              </button>
              <button onClick={submitRepush} disabled={!!repushing}
                data-testid="failed-deliveries-repush-confirm"
                className="bg-emerald-700 border border-emerald-700 text-white px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40">
                {repushing ? "Repushing…" : "Confirm repush"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
